import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormInput from './FormInput';
import { useForm } from '../hooks/form';
import toast from 'react-hot-toast';

const BeneficiaryModal = ({ isOpen, onClose, onSave, beneficiaryToEdit = null }) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  
  const initialFormState = {
    name: '',
    last_name: '',
    phone_number: '',
    email: '',
    identifier: '',
  };
  
  const { form, handleOnChange, errors, setErrors, setForm, post, put, loading } = useForm(initialFormState);

  // Set edit mode and populate form when beneficiaryToEdit changes
  useEffect(() => {
    if (beneficiaryToEdit) {
      setIsEditMode(true);
      setForm({
        name: beneficiaryToEdit.name || '',
        last_name: beneficiaryToEdit.last_name || '',
        phone_number: beneficiaryToEdit.phone_number || '',
        email: beneficiaryToEdit.email || '',
        identifier: beneficiaryToEdit.identifier || '',
      });
    } else {
      setIsEditMode(false);
    }
  }, [beneficiaryToEdit]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      resetForm();
    }
  }, [isOpen]);
  
  const resetForm = () => {
    setForm(initialFormState);
    setErrors({});
    setIsEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (isEditMode && beneficiaryToEdit) {
        // Update existing beneficiary
        response = await put(`/api/beneficiaries/${beneficiaryToEdit.id}`, form);
      } else {
        // Create new beneficiary
        response = await post('/api/beneficiaries');
      }
      
      // Pass the created/edited beneficiary to the parent component
      if (response && response.data) {
        onSave(response.data);
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'add'} beneficiary:`, error);
      // Display a toast error message
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t(`checkout.${isEditMode ? 'beneficiary_update_error' : 'beneficiary_create_error'}`));
      }
      // Error handling is automatically done by the post/put method
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center">
      {/* Backdrop */}
      <div 
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="tw-relative tw-bg-white tw-rounded-lg tw-p-6 tw-max-w-md tw-w-full tw-mx-4 tw-z-10">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <h3 className="tw-text-xl tw-font-semibold">
            {isEditMode ? t('checkout.edit_beneficiary') : t('checkout.add_new_beneficiary')}
          </h3>
          <button 
            onClick={onClose}
            className="tw-text-gray-500 hover:tw-text-gray-700 tw-text-2xl"
          >
            &times;
          </button>
        </div>
        
        {errors?.general && (
          <div className="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4">
            {errors.general}
          </div>
        )}
        
        <form onSubmit={handleSubmit} noValidate>
          <div className="tw-space-y-3">
            <FormInput
              label={t('checkout.name')}
              value={form.name}
              name="name"
              onChange={handleOnChange}
              errors={errors?.name}
              required
            />
            <FormInput
              label={t('checkout.last_name')}
              value={form.last_name}
              name="last_name"
              onChange={handleOnChange}
              errors={errors?.last_name}
              required
            />
            <FormInput
              label={t('checkout.phone')}
              value={form.phone_number}
              name="phone_number"
              onChange={handleOnChange}
              errors={errors?.phone_number}
              required
            />
            <FormInput
              label={t('checkout.email')}
              value={form.email}
              name="email"
              onChange={handleOnChange}
              errors={errors?.email}
              type="email"
              required
            />
            <FormInput
              label={t('checkout.identify_number')}
              value={form.identifier}
              name="identifier"
              onChange={handleOnChange}
              errors={errors?.identifier}
              required
            />
          </div>
          
          <div className="tw-mt-6 tw-flex tw-justify-end tw-space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-rounded-md"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-24 py-10 btn btn-main rounded-8"
              disabled={loading}
            >
              {loading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BeneficiaryModal; 